/**
 * Star toggle to follow/unfollow a hospital. Anywhere a hospital appears (nearby
 * list, profile, rankings) this lets the user add it to their shortlist, which
 * powers the /followed planning page. Stays in sync across instances via the
 * hospitalStorage change event.
 */
import React, { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { hospitalStorage } from "@/lib/hospitalStorage";

interface Props {
  hospital: { id: string; name: string; city: string; state: string };
  dark?: boolean;
  size?: number;
  className?: string;
  /** Show a "Follow"/"Following" text label next to the star. */
  withLabel?: boolean;
}

export default function FollowHospitalButton({ hospital, dark, size = 16, className = "", withLabel = false }: Props) {
  const [followed, setFollowed] = useState(false);

  useEffect(() => {
    const sync = () => setFollowed(hospitalStorage.isFollowed(hospital.id));
    sync();
    window.addEventListener("ahp-followed-changed", sync);
    return () => window.removeEventListener("ahp-followed-changed", sync);
  }, [hospital.id]);

  const gold = "#C5A55A";
  const idle = dark ? "rgba(240,235,227,0.55)" : "rgba(27,27,27,0.45)";

  return (
    <button
      type="button"
      aria-label={followed ? "Unfollow hospital" : "Follow hospital"}
      aria-pressed={followed}
      title={followed ? "Following — tap to remove" : "Follow this hospital"}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setFollowed(hospitalStorage.toggle(hospital));
      }}
      className={`inline-flex items-center gap-1 transition-colors ${className}`}
      style={{ color: followed ? gold : idle }}
    >
      <Star style={{ width: size, height: size }} fill={followed ? gold : "none"} strokeWidth={2} />
      {withLabel && <span style={{ fontSize: 12, fontWeight: 600 }}>{followed ? "Following" : "Follow"}</span>}
    </button>
  );
}
